'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function HouseAffordabilityCalculator() {
  const t = useTranslations("tools.houseAffordabilityCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Income-based calculator state
  const [incomeInputs, setIncomeInputs] = useState({
    annualIncome: 120000,
    loanTerm: 30,
    interestRate: 6.286,
    monthlyDebt: 0,
    downPayment: 20,
    downPaymentUnit: 'percent' as 'percent' | 'dollar',
    propertyTax: 1.5,
    propertyTaxUnit: 'percent' as 'percent' | 'dollar',
    hoaFee: 0,
    hoaFeeUnit: 'percent' as 'percent' | 'dollar',
    insurance: 0.5,
    insuranceUnit: 'percent' as 'percent' | 'dollar',
    dtiRatio: 'conventional',
  });

  // Budget-based calculator state
  const [budgetInputs, setBudgetInputs] = useState({
    monthlyBudget: 3500,
    loanTerm: 30,
    interestRate: 6.286,
    downPayment: 20,
    downPaymentUnit: 'percent' as 'percent' | 'dollar',
    includeFees: true,
    propertyTax: 1.5,
    propertyTaxUnit: 'percent' as 'percent' | 'dollar',
    hoaFee: 0,
    hoaFeeUnit: 'percent' as 'percent' | 'dollar',
    insurance: 0.5,
    insuranceUnit: 'percent' as 'percent' | 'dollar',
    maintenanceCost: 1.5,
    maintenanceCostUnit: 'percent' as 'percent' | 'dollar',
  });

  const [incomeResults, setIncomeResults] = useState<any>(null);
  const [budgetResults, setBudgetResults] = useState<any>(null);
  const [activeCalculator, setActiveCalculator] = useState<'income' | 'budget' | null>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const styles = {
    container: getResponsiveStyle(
      { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
      { padding: '1rem' },
      { padding: '1.5rem' },
      { padding: '2rem' }
    ),
    header: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '2rem' },
      {},
      { marginBottom: '2.5rem' },
      { marginBottom: '3rem' }
    ),
    h1: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
      { fontSize: '2rem' },
      { fontSize: '2.5rem' },
      { fontSize: '3rem' }
    ),
    summaryText: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6 },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    calculatorTool: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
        marginBottom: '2rem',
        borderRadius: '1rem',
      },
      { padding: '1.5rem 1rem' },
      { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
      { padding: '3rem 1.5rem', borderRadius: '1.5rem' }
    ),
    toolHeader: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '1.5rem' },
      {},
      { marginBottom: '2rem' },
      { marginBottom: '2.5rem' }
    ),
    h2: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),
    h3: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' },
      { fontSize: '1.25rem' },
      { fontSize: '1.375rem' },
      { fontSize: '1.5rem' }
    ),
    ratesBox: getResponsiveStyle(
      {
        background: '#fef3c7',
        border: '2px solid #fbbf24',
        borderRadius: '0.5rem',
        padding: '1rem',
        marginBottom: '1.5rem',
        textAlign: 'center' as const,
      },
      { fontSize: '0.875rem' },
      { fontSize: '0.95rem' },
      { fontSize: '1rem' }
    ),
    rateLink: {
      color: '#2563eb',
      textDecoration: 'none',
      fontWeight: 600,
      cursor: 'pointer',
    },
    calculatorGrid: getResponsiveStyle(
      { display: 'grid', gap: '2rem', marginBottom: '2rem' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: '1fr 1fr' }
    ),
    calculatorCard: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      },
      { padding: '1.5rem' },
      { padding: '1.75rem' },
      { padding: '2rem' }
    ),
    formGroup: {
      marginBottom: '1.5rem',
    },
    label: getResponsiveStyle(
      { display: 'block', fontWeight: 600, color: '#334155', marginBottom: '0.5rem' },
      { fontSize: '0.875rem' },
      { fontSize: '0.9rem' },
      { fontSize: '0.95rem' }
    ),
    input: getResponsiveStyle(
      {
        width: '100%',
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        transition: 'all 0.3s ease',
        boxSizing: 'border-box' as const,
      },
      { padding: '0.75rem 1rem', fontSize: '1rem' },
      { padding: '0.8rem 1.1rem', fontSize: '1.05rem' },
      { padding: '0.875rem 1.25rem', fontSize: '1.1rem' }
    ),
    select: getResponsiveStyle(
      {
        width: '100%',
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        transition: 'all 0.3s ease',
        boxSizing: 'border-box' as const,
        background: 'white',
      },
      { padding: '0.75rem 1rem', fontSize: '1rem' },
      { padding: '0.8rem 1.1rem', fontSize: '1.05rem' },
      { padding: '0.875rem 1.25rem', fontSize: '1.1rem' }
    ),
    inputWithUnit: {
      display: 'flex',
      gap: '0.5rem',
      alignItems: 'center',
    },
    unitSelect: getResponsiveStyle(
      {
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        background: 'white',
        minWidth: '80px',
      },
      { padding: '0.75rem 0.5rem', fontSize: '1rem' },
      { padding: '0.8rem 0.6rem', fontSize: '1.05rem' },
      { padding: '0.875rem 0.75rem', fontSize: '1.1rem' }
    ),
    helpIcon: {
      display: 'inline-block',
      width: '18px',
      height: '18px',
      background: '#94a3b8',
      color: 'white',
      borderRadius: '50%',
      textAlign: 'center' as const,
      lineHeight: '18px',
      fontSize: '12px',
      cursor: 'help',
      marginLeft: '0.25rem',
    },
    calculateButton: getResponsiveStyle(
      {
        width: '100%',
        fontWeight: 600,
        color: 'white',
        background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
        border: 'none',
        borderRadius: '0.5rem',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        marginTop: '1rem',
      },
      { padding: '0.875rem 1.5rem', fontSize: '1rem' },
      { padding: '0.95rem 1.75rem', fontSize: '1.05rem' },
      { padding: '1rem 2rem', fontSize: '1.1rem' }
    ),
    clearButton: getResponsiveStyle(
      {
        width: '100%',
        fontWeight: 600,
        background: '#f1f5f9',
        color: '#475569',
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        marginTop: '0.5rem',
      },
      { padding: '0.75rem 1.25rem', fontSize: '0.95rem' },
      { padding: '0.8rem 1.5rem', fontSize: '1rem' },
      { padding: '0.875rem 1.75rem', fontSize: '1.05rem' }
    ),
    checkboxGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      margin: '1rem 0',
    },
    checkbox: {
      width: 'auto',
      margin: 0,
      cursor: 'pointer',
    },
    checkboxLabel: {
      margin: 0,
      fontWeight: 500,
      cursor: 'pointer',
    },
    results: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        marginTop: '2rem',
      },
      { padding: '1.5rem' },
      { padding: '1.75rem' },
      { padding: '2rem' }
    ),
    resultsTitle: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, marginBottom: '1.5rem', textAlign: 'center' as const },
      { fontSize: '1.5rem' },
      { fontSize: '1.625rem' },
      { fontSize: '1.75rem' }
    ),
    resultHighlight: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
        color: 'white',
        borderRadius: '0.75rem',
        textAlign: 'center' as const,
        marginBottom: '2rem',
      },
      { padding: '1.5rem' },
      { padding: '1.75rem' },
      { padding: '2rem' }
    ),
    highlightLabel: getResponsiveStyle(
      { opacity: 0.9, marginBottom: '0.5rem' },
      { fontSize: '0.95rem' },
      { fontSize: '1rem' },
      { fontSize: '1.05rem' }
    ),
    highlightValue: getResponsiveStyle(
      { fontWeight: 700 },
      { fontSize: '2rem' },
      { fontSize: '2.25rem' },
      { fontSize: '2.5rem' }
    ),
    resultGrid: getResponsiveStyle(
      { display: 'grid', gap: '1.5rem', marginBottom: '2rem' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: 'repeat(2, 1fr)' },
      { gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }
    ),
    resultItem: getResponsiveStyle(
      {
        background: '#f8fafc',
        borderRadius: '0.5rem',
        borderLeft: '4px solid #2563eb',
      },
      { padding: '1rem' },
      { padding: '1.125rem' },
      { padding: '1.25rem' }
    ),
    resultItemLabel: getResponsiveStyle(
      { color: '#64748b', marginBottom: '0.5rem', fontWeight: 500 },
      { fontSize: '0.85rem' },
      { fontSize: '0.875rem' },
      { fontSize: '0.9rem' }
    ),
    resultItemValue: getResponsiveStyle(
      { color: colors.textPrimary, fontWeight: 700 },
      { fontSize: '1.25rem' },
      { fontSize: '1.375rem' },
      { fontSize: '1.5rem' }
    ),
    breakdownTable: {
      width: '100%',
      borderCollapse: 'collapse' as const,
      marginTop: '1.5rem',
    },
    tableHeader: getResponsiveStyle(
      { background: '#f8fafc', fontWeight: 600, color: '#475569', textAlign: 'left' as const, borderBottom: '1px solid #e2e8f0' },
      { padding: '0.75rem' },
      { padding: '0.875rem' },
      { padding: '1rem' }
    ),
    tableCell: getResponsiveStyle(
      { color: colors.textPrimary, borderBottom: '1px solid #e2e8f0', textAlign: 'left' as const },
      { padding: '0.75rem' },
      { padding: '0.875rem' },
      { padding: '1rem' }
    ),
    glassCard: getResponsiveStyle(
      {
        background: 'rgba(255, 255, 255, 0.6)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: '1px solid rgba(255, 200, 180, 0.2)',
        borderRadius: '1rem',
        boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
        marginBottom: '2rem',
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2.5rem' }
    ),
    paragraph: getResponsiveStyle(
      { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem', lineHeight: 1.7 },
      { fontSize: '1.25rem', lineHeight: 1.8 }
    ),
    list: getResponsiveStyle(
      { marginLeft: '1.5rem', marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    listItem: {
      marginBottom: '0.75rem',
      lineHeight: 1.6,
      color: colors.textSecondary,
    },
    insightCard: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.05) 0%, rgba(109, 40, 217, 0.05) 100%)',
        borderLeft: '4px solid #7c3aed',
        borderRadius: '0.5rem',
        marginBottom: '1.5rem',
      },
      { padding: '1rem' },
      { padding: '1.25rem' },
      { padding: '1.5rem' }
    ),
    faqItem: {
      marginBottom: '1.5rem',
    },
    faqQuestion: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
      { fontSize: '1.05rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.2rem' }
    ),
    faqAnswer: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6 },
      { fontSize: '0.95rem' },
      { fontSize: '1rem' },
      { fontSize: '1.05rem' }
    ),
    referenceList: {
      listStyle: 'none',
      padding: 0,
    },
    referenceItem: {
      marginBottom: '0.75rem',
    },
    referenceLink: {
      color: '#2563eb',
      textDecoration: 'none',
      wordBreak: 'break-all' as const,
    },
  };

  const formatCurrency = (value: number) => {
    return '$' + Math.round(value).toLocaleString('en-US');
  };

  const formatPercent = (value: number) => {
    return value.toFixed(2) + '%';
  };

  const calculateMonthlyPayment = (principal: number, annualRate: number, years: number) => {
    const monthlyRate = annualRate / 100 / 12;
    const numPayments = years * 12;
    if (monthlyRate === 0) return principal / numPayments;
    return principal * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / (Math.pow(1 + monthlyRate, numPayments) - 1);
  };

  const getDTILimits = (dtiType: string) => {
    switch(dtiType) {
      case 'conventional':
        return { front: 28, back: 36 };
      case 'fha':
        return { front: 31, back: 43 };
      case 'va':
        return { front: 41, back: 41 };
      default:
        const limit = parseFloat(dtiType);
        return { front: limit, back: limit };
    }
  };

  const calculateValue = (input: number, unit: 'percent' | 'dollar', baseValue: number) => {
    if (unit === 'percent') {
      return baseValue * input / 100;
    }
    return input;
  };

  const handleIncomeCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const annualIncome = incomeInputs.annualIncome;
    const monthlyIncome = annualIncome / 12;
    const loanTerm = incomeInputs.loanTerm;
    const interestRate = incomeInputs.interestRate;
    const monthlyDebt = incomeInputs.monthlyDebt;

    const dtiLimits = getDTILimits(incomeInputs.dtiRatio);
    const maxHousingPayment = monthlyIncome * dtiLimits.front / 100;
    const maxTotalPayment = monthlyIncome * dtiLimits.back / 100;
    const maxHousingWithDebt = maxTotalPayment - monthlyDebt;
    const maxMonthlyPayment = Math.min(maxHousingPayment, maxHousingWithDebt);

    let homePrice = 100000;
    let bestPrice = 0;
    let step = 50000;

    for (let iteration = 0; iteration < 20; iteration++) {
      let downPayment = incomeInputs.downPaymentUnit === 'percent'
        ? homePrice * incomeInputs.downPayment / 100
        : incomeInputs.downPayment;

      const loanAmount = homePrice - downPayment;
      const principalInterest = calculateMonthlyPayment(loanAmount, interestRate, loanTerm);

      const propertyTax = calculateValue(incomeInputs.propertyTax, incomeInputs.propertyTaxUnit, homePrice) / 12;
      const hoa = calculateValue(incomeInputs.hoaFee, incomeInputs.hoaFeeUnit, homePrice) / 12;
      const insurance = calculateValue(incomeInputs.insurance, incomeInputs.insuranceUnit, homePrice) / 12;

      const totalMonthly = principalInterest + propertyTax + hoa + insurance;

      if (totalMonthly <= maxMonthlyPayment) {
        bestPrice = homePrice;
        homePrice += step;
      } else {
        homePrice -= step;
        step /= 2;
      }
    }

    homePrice = bestPrice;
    const downPayment = incomeInputs.downPaymentUnit === 'percent'
      ? homePrice * incomeInputs.downPayment / 100
      : incomeInputs.downPayment;

    const loanAmount = homePrice - downPayment;
    const principalInterest = calculateMonthlyPayment(loanAmount, interestRate, loanTerm);
    const propertyTax = calculateValue(incomeInputs.propertyTax, incomeInputs.propertyTaxUnit, homePrice) / 12;
    const hoa = calculateValue(incomeInputs.hoaFee, incomeInputs.hoaFeeUnit, homePrice) / 12;
    const insurance = calculateValue(incomeInputs.insurance, incomeInputs.insuranceUnit, homePrice) / 12;

    const totalMonthly = principalInterest + propertyTax + hoa + insurance;
    const frontDTI = (totalMonthly / monthlyIncome) * 100;
    const backDTI = ((totalMonthly + monthlyDebt) / monthlyIncome) * 100;

    setIncomeResults({
      homePrice,
      totalMonthly,
      loanAmount,
      downPayment,
      frontDTI,
      backDTI,
      breakdown: {
        principalInterest,
        propertyTax,
        hoa,
        insurance,
      }
    });

    setActiveCalculator('income');
  };

  const handleBudgetCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const monthlyBudget = budgetInputs.monthlyBudget;
    const loanTerm = budgetInputs.loanTerm;
    const interestRate = budgetInputs.interestRate;
    const includeFees = budgetInputs.includeFees;

    let homePrice = 100000;
    let bestPrice = 0;
    let step = 50000;

    for (let iteration = 0; iteration < 20; iteration++) {
      const downPayment = budgetInputs.downPaymentUnit === 'percent'
        ? homePrice * budgetInputs.downPayment / 100
        : budgetInputs.downPayment;

      const loanAmount = homePrice - downPayment;
      const principalInterest = calculateMonthlyPayment(loanAmount, interestRate, loanTerm);

      let totalMonthly = principalInterest;

      if (includeFees) {
        const propertyTax = calculateValue(budgetInputs.propertyTax, budgetInputs.propertyTaxUnit, homePrice) / 12;
        const hoa = calculateValue(budgetInputs.hoaFee, budgetInputs.hoaFeeUnit, homePrice) / 12;
        const insurance = calculateValue(budgetInputs.insurance, budgetInputs.insuranceUnit, homePrice) / 12;
        const maintenance = calculateValue(budgetInputs.maintenanceCost, budgetInputs.maintenanceCostUnit, homePrice) / 12;

        totalMonthly += propertyTax + hoa + insurance + maintenance;
      }

      if (totalMonthly <= monthlyBudget) {
        bestPrice = homePrice;
        homePrice += step;
      } else {
        homePrice -= step;
        step /= 2;
      }
    }

    homePrice = bestPrice;
    const downPayment = budgetInputs.downPaymentUnit === 'percent'
      ? homePrice * budgetInputs.downPayment / 100
      : budgetInputs.downPayment;

    const loanAmount = homePrice - downPayment;
    const principalInterest = calculateMonthlyPayment(loanAmount, interestRate, loanTerm);

    let breakdown: any = { principalInterest };
    let totalMonthly = principalInterest;

    if (includeFees) {
      const propertyTax = calculateValue(budgetInputs.propertyTax, budgetInputs.propertyTaxUnit, homePrice) / 12;
      const hoa = calculateValue(budgetInputs.hoaFee, budgetInputs.hoaFeeUnit, homePrice) / 12;
      const insurance = calculateValue(budgetInputs.insurance, budgetInputs.insuranceUnit, homePrice) / 12;
      const maintenance = calculateValue(budgetInputs.maintenanceCost, budgetInputs.maintenanceCostUnit, homePrice) / 12;

      totalMonthly += propertyTax + hoa + insurance + maintenance;
      breakdown = { ...breakdown, propertyTax, hoa, insurance, maintenance };
    }

    setBudgetResults({
      homePrice,
      monthlyBudget,
      loanAmount,
      downPayment,
      totalMonthly,
      breakdown,
      includeFees,
    });

    setActiveCalculator('budget');
  };

  const clearIncomeForm = () => {
    setIncomeInputs({
      annualIncome: 120000,
      loanTerm: 30,
      interestRate: 6.286,
      monthlyDebt: 0,
      downPayment: 20,
      downPaymentUnit: 'percent',
      propertyTax: 1.5,
      propertyTaxUnit: 'percent',
      hoaFee: 0,
      hoaFeeUnit: 'percent',
      insurance: 0.5,
      insuranceUnit: 'percent',
      dtiRatio: 'conventional',
    });
    setIncomeResults(null);
    setActiveCalculator(null);
  };

  const clearBudgetForm = () => {
    setBudgetInputs({
      monthlyBudget: 3500,
      loanTerm: 30,
      interestRate: 6.286,
      downPayment: 20,
      downPaymentUnit: 'percent',
      includeFees: true,
      propertyTax: 1.5,
      propertyTaxUnit: 'percent',
      hoaFee: 0,
      hoaFeeUnit: 'percent',
      insurance: 0.5,
      insuranceUnit: 'percent',
      maintenanceCost: 1.5,
      maintenanceCostUnit: 'percent',
    });
    setBudgetResults(null);
    setActiveCalculator(null);
  };

  const setRate = (rate: number, years: number) => {
    setIncomeInputs({ ...incomeInputs, interestRate: rate, loanTerm: years });
    setBudgetInputs({ ...budgetInputs, interestRate: rate, loanTerm: years });
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.h1}>{t("title")}</h1>
        <p style={styles.summaryText}>{t("subtitle")}</p>
      </header>

      <section style={styles.calculatorTool}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={styles.toolHeader}>
            <h2 style={styles.h2}>{t("toolTitle")}</h2>
            <p style={styles.summaryText}>{t("toolSubtitle")}</p>
          </div>

          <div style={styles.ratesBox}>
            <strong>{t("latestRates")}</strong>{' '}
            {t("thirtyYears")}: <span style={styles.rateLink} onClick={() => setRate(6.286, 30)}>6.286%</span> |{' '}
            {t("fifteenYears")}: <span style={styles.rateLink} onClick={() => setRate(5.413, 15)}>5.413%</span> |{' '}
            {t("tenYears")}: <span style={styles.rateLink} onClick={() => setRate(5.25, 10)}>5.25%</span>
          </div>

          <div style={styles.calculatorGrid}>
            <div style={styles.calculatorCard}>
              <h3 style={styles.h3}>💰 {t("incomeBasedTitle")}</h3>
              <form onSubmit={handleIncomeCalculate}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    {t("annualIncome")} <span style={styles.helpIcon} title={t("annualIncomeHelp")}>?</span>
                  </label>
                  <input
                    type="number"
                    style={styles.input}
                    value={incomeInputs.annualIncome}
                    onChange={(e) => setIncomeInputs({...incomeInputs, annualIncome: parseFloat(e.target.value) || 0})}
                    step="1000"
                    required
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>{t("loanTerm")}</label>
                  <div style={styles.inputWithUnit}>
                    <input
                      type="number"
                      style={{...styles.input, flex: 1}}
                      value={incomeInputs.loanTerm}
                      onChange={(e) => setIncomeInputs({...incomeInputs, loanTerm: parseFloat(e.target.value) || 0})}
                      min="1"
                      max="40"
                      required
                    />
                    <select style={styles.unitSelect} disabled>
                      <option>{t("years")}</option>
                    </select>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>{t("interestRate")}</label>
                  <div style={styles.inputWithUnit}>
                    <input
                      type="number"
                      style={{...styles.input, flex: 1}}
                      value={incomeInputs.interestRate}
                      onChange={(e) => setIncomeInputs({...incomeInputs, interestRate: parseFloat(e.target.value) || 0})}
                      step="0.001"
                      min="0"
                      max="20"
                      required
                    />
                    <select style={styles.unitSelect} disabled>
                      <option>%</option>
                    </select>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    {t("monthlyDebt")} <span style={styles.helpIcon} title={t("monthlyDebtHelp")}>?</span>
                  </label>
                  <input
                    type="number"
                    style={styles.input}
                    value={incomeInputs.monthlyDebt}
                    onChange={(e) => setIncomeInputs({...incomeInputs, monthlyDebt: parseFloat(e.target.value) || 0})}
                    step="100"
                    min="0"
                    required
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>{t("downPayment")}</label>
                  <div style={styles.inputWithUnit}>
                    <input
                      type="number"
                      style={{...styles.input, flex: 1}}
                      value={incomeInputs.downPayment}
                      onChange={(e) => setIncomeInputs({...incomeInputs, downPayment: parseFloat(e.target.value) || 0})}
                      step="0.1"
                      min="0"
                      required
                    />
                    <select
                      style={styles.unitSelect}
                      value={incomeInputs.downPaymentUnit}
                      onChange={(e) => setIncomeInputs({...incomeInputs, downPaymentUnit: e.target.value as 'percent' | 'dollar'})}
                    >
                      <option value="percent">%</option>
                      <option value="dollar">$</option>
                    </select>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>{t("propertyTax")}</label>
                  <div style={styles.inputWithUnit}>
                    <input
                      type="number"
                      style={{...styles.input, flex: 1}}
                      value={incomeInputs.propertyTax}
                      onChange={(e) => setIncomeInputs({...incomeInputs, propertyTax: parseFloat(e.target.value) || 0})}
                      step="0.1"
                      min="0"
                      required
                    />
                    <select
                      style={styles.unitSelect}
                      value={incomeInputs.propertyTaxUnit}
                      onChange={(e) => setIncomeInputs({...incomeInputs, propertyTaxUnit: e.target.value as 'percent' | 'dollar'})}
                    >
                      <option value="percent">%</option>
                      <option value="dollar">$</option>
                    </select>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>{t("hoaFee")}</label>
                  <div style={styles.inputWithUnit}>
                    <input
                      type="number"
                      style={{...styles.input, flex: 1}}
                      value={incomeInputs.hoaFee}
                      onChange={(e) => setIncomeInputs({...incomeInputs, hoaFee: parseFloat(e.target.value) || 0})}
                      step="100"
                      min="0"
                      required
                    />
                    <select
                      style={styles.unitSelect}
                      value={incomeInputs.hoaFeeUnit}
                      onChange={(e) => setIncomeInputs({...incomeInputs, hoaFeeUnit: e.target.value as 'percent' | 'dollar'})}
                    >
                      <option value="percent">%</option>
                      <option value="dollar">$</option>
                    </select>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>{t("insurance")}</label>
                  <div style={styles.inputWithUnit}>
                    <input
                      type="number"
                      style={{...styles.input, flex: 1}}
                      value={incomeInputs.insurance}
                      onChange={(e) => setIncomeInputs({...incomeInputs, insurance: parseFloat(e.target.value) || 0})}
                      step="0.1"
                      min="0"
                      required
                    />
                    <select
                      style={styles.unitSelect}
                      value={incomeInputs.insuranceUnit}
                      onChange={(e) => setIncomeInputs({...incomeInputs, insuranceUnit: e.target.value as 'percent' | 'dollar'})}
                    >
                      <option value="percent">%</option>
                      <option value="dollar">$</option>
                    </select>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>{t("dtiRatio")}</label>
                  <select
                    style={styles.select}
                    value={incomeInputs.dtiRatio}
                    onChange={(e) => setIncomeInputs({...incomeInputs, dtiRatio: e.target.value})}
                  >
                    <option value="conventional">{t("conventionalLoan")}</option>
                    <option value="fha">{t("fhaLoan")}</option>
                    <option value="va">{t("vaLoan")}</option>
                    <option value="10">10%</option>
                    <option value="15">15%</option>
                    <option value="20">20%</option>
                    <option value="25">25%</option>
                    <option value="30">30%</option>
                    <option value="35">35%</option>
                    <option value="40">40%</option>
                    <option value="45">45%</option>
                    <option value="50">50%</option>
                  </select>
                </div>

                <button type="submit" style={styles.calculateButton}>
                  {t("calculateAffordability")}
                </button>
                <button type="button" style={styles.clearButton} onClick={clearIncomeForm}>
                  {t("clear")}
                </button>
              </form>
            </div>

            <div style={styles.calculatorCard}>
              <h3 style={styles.h3}>📊 {t("budgetBasedTitle")}</h3>
              <form onSubmit={handleBudgetCalculate}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    {t("monthlyBudget")} <span style={styles.helpIcon} title={t("monthlyBudgetHelp")}>?</span>
                  </label>
                  <input
                    type="number"
                    style={styles.input}
                    value={budgetInputs.monthlyBudget}
                    onChange={(e) => setBudgetInputs({...budgetInputs, monthlyBudget: parseFloat(e.target.value) || 0})}
                    step="100"
                    min="0"
                    required
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>{t("loanTerm")}</label>
                  <div style={styles.inputWithUnit}>
                    <input
                      type="number"
                      style={{...styles.input, flex: 1}}
                      value={budgetInputs.loanTerm}
                      onChange={(e) => setBudgetInputs({...budgetInputs, loanTerm: parseFloat(e.target.value) || 0})}
                      min="1"
                      max="40"
                      required
                    />
                    <select style={styles.unitSelect} disabled>
                      <option>{t("years")}</option>
                    </select>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>{t("interestRate")}</label>
                  <div style={styles.inputWithUnit}>
                    <input
                      type="number"
                      style={{...styles.input, flex: 1}}
                      value={budgetInputs.interestRate}
                      onChange={(e) => setBudgetInputs({...budgetInputs, interestRate: parseFloat(e.target.value) || 0})}
                      step="0.001"
                      min="0"
                      max="20"
                      required
                    />
                    <select style={styles.unitSelect} disabled>
                      <option>%</option>
                    </select>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>{t("downPayment")}</label>
                  <div style={styles.inputWithUnit}>
                    <input
                      type="number"
                      style={{...styles.input, flex: 1}}
                      value={budgetInputs.downPayment}
                      onChange={(e) => setBudgetInputs({...budgetInputs, downPayment: parseFloat(e.target.value) || 0})}
                      step="0.1"
                      min="0"
                      required
                    />
                    <select
                      style={styles.unitSelect}
                      value={budgetInputs.downPaymentUnit}
                      onChange={(e) => setBudgetInputs({...budgetInputs, downPaymentUnit: e.target.value as 'percent' | 'dollar'})}
                    >
                      <option value="percent">%</option>
                      <option value="dollar">$</option>
                    </select>
                  </div>
                </div>

                <div style={styles.checkboxGroup}>
                  <input
                    type="checkbox"
                    id="include-fees"
                    style={styles.checkbox}
                    checked={budgetInputs.includeFees}
                    onChange={(e) => setBudgetInputs({...budgetInputs, includeFees: e.target.checked})}
                  />
                  <label htmlFor="include-fees" style={styles.checkboxLabel}>
                    {t("includeFeesInBudget")}
                  </label>
                </div>

                {budgetInputs.includeFees && (
                  <>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>{t("propertyTax")}</label>
                      <div style={styles.inputWithUnit}>
                        <input
                          type="number"
                          style={{...styles.input, flex: 1}}
                          value={budgetInputs.propertyTax}
                          onChange={(e) => setBudgetInputs({...budgetInputs, propertyTax: parseFloat(e.target.value) || 0})}
                          step="0.1"
                          min="0"
                        />
                        <select
                          style={styles.unitSelect}
                          value={budgetInputs.propertyTaxUnit}
                          onChange={(e) => setBudgetInputs({...budgetInputs, propertyTaxUnit: e.target.value as 'percent' | 'dollar'})}
                        >
                          <option value="percent">%</option>
                          <option value="dollar">$</option>
                        </select>
                      </div>
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.label}>{t("hoaFee")}</label>
                      <div style={styles.inputWithUnit}>
                        <input
                          type="number"
                          style={{...styles.input, flex: 1}}
                          value={budgetInputs.hoaFee}
                          onChange={(e) => setBudgetInputs({...budgetInputs, hoaFee: parseFloat(e.target.value) || 0})}
                          step="100"
                          min="0"
                        />
                        <select
                          style={styles.unitSelect}
                          value={budgetInputs.hoaFeeUnit}
                          onChange={(e) => setBudgetInputs({...budgetInputs, hoaFeeUnit: e.target.value as 'percent' | 'dollar'})}
                        >
                          <option value="percent">%</option>
                          <option value="dollar">$</option>
                        </select>
                      </div>
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.label}>{t("insurance")}</label>
                      <div style={styles.inputWithUnit}>
                        <input
                          type="number"
                          style={{...styles.input, flex: 1}}
                          value={budgetInputs.insurance}
                          onChange={(e) => setBudgetInputs({...budgetInputs, insurance: parseFloat(e.target.value) || 0})}
                          step="0.1"
                          min="0"
                        />
                        <select
                          style={styles.unitSelect}
                          value={budgetInputs.insuranceUnit}
                          onChange={(e) => setBudgetInputs({...budgetInputs, insuranceUnit: e.target.value as 'percent' | 'dollar'})}
                        >
                          <option value="percent">%</option>
                          <option value="dollar">$</option>
                        </select>
                      </div>
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.label}>{t("maintenanceCost")}</label>
                      <div style={styles.inputWithUnit}>
                        <input
                          type="number"
                          style={{...styles.input, flex: 1}}
                          value={budgetInputs.maintenanceCost}
                          onChange={(e) => setBudgetInputs({...budgetInputs, maintenanceCost: parseFloat(e.target.value) || 0})}
                          step="0.1"
                          min="0"
                        />
                        <select
                          style={styles.unitSelect}
                          value={budgetInputs.maintenanceCostUnit}
                          onChange={(e) => setBudgetInputs({...budgetInputs, maintenanceCostUnit: e.target.value as 'percent' | 'dollar'})}
                        >
                          <option value="percent">%</option>
                          <option value="dollar">$</option>
                        </select>
                      </div>
                    </div>
                  </>
                )}

                <button type="submit" style={styles.calculateButton}>
                  {t("calculateAffordability")}
                </button>
                <button type="button" style={styles.clearButton} onClick={clearBudgetForm}>
                  {t("clear")}
                </button>
              </form>
            </div>
          </div>

          {activeCalculator === 'income' && incomeResults && (
            <div style={styles.results}>
              <h3 style={styles.resultsTitle}>{t("incomeResultsTitle")}</h3>
              <div style={styles.resultHighlight}>
                <div style={styles.highlightLabel}>{t("maxAffordablePrice")}</div>
                <div style={styles.highlightValue}>{formatCurrency(incomeResults.homePrice)}</div>
              </div>

              <div style={styles.resultGrid}>
                <div style={styles.resultItem}>
                  <div style={styles.resultItemLabel}>{t("monthlyPayment")}</div>
                  <div style={styles.resultItemValue}>{formatCurrency(incomeResults.totalMonthly)}</div>
                </div>
                <div style={styles.resultItem}>
                  <div style={styles.resultItemLabel}>{t("loanAmount")}</div>
                  <div style={styles.resultItemValue}>{formatCurrency(incomeResults.loanAmount)}</div>
                </div>
                <div style={styles.resultItem}>
                  <div style={styles.resultItemLabel}>{t("downPaymentAmount")}</div>
                  <div style={styles.resultItemValue}>{formatCurrency(incomeResults.downPayment)}</div>
                </div>
                <div style={styles.resultItem}>
                  <div style={styles.resultItemLabel}>{t("frontEndDTI")}</div>
                  <div style={styles.resultItemValue}>{formatPercent(incomeResults.frontDTI)}</div>
                </div>
                <div style={styles.resultItem}>
                  <div style={styles.resultItemLabel}>{t("backEndDTI")}</div>
                  <div style={styles.resultItemValue}>{formatPercent(incomeResults.backDTI)}</div>
                </div>
              </div>

              <table style={styles.breakdownTable}>
                <thead>
                  <tr>
                    <th style={styles.tableHeader}>{t("monthlyCostBreakdown")}</th>
                    <th style={styles.tableHeader}>{t("amount")}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={styles.tableCell}>{t("principalInterest")}</td>
                    <td style={styles.tableCell}>{formatCurrency(incomeResults.breakdown.principalInterest)}</td>
                  </tr>
                  <tr>
                    <td style={styles.tableCell}>{t("propertyTaxMonthly")}</td>
                    <td style={styles.tableCell}>{formatCurrency(incomeResults.breakdown.propertyTax)}</td>
                  </tr>
                  <tr>
                    <td style={styles.tableCell}>{t("hoaFeeMonthly")}</td>
                    <td style={styles.tableCell}>{formatCurrency(incomeResults.breakdown.hoa)}</td>
                  </tr>
                  <tr>
                    <td style={styles.tableCell}>{t("insuranceMonthly")}</td>
                    <td style={styles.tableCell}>{formatCurrency(incomeResults.breakdown.insurance)}</td>
                  </tr>
                  <tr>
                    <td style={{...styles.tableCell, borderBottom: 'none'}}><strong>{t("totalMonthlyPayment")}</strong></td>
                    <td style={{...styles.tableCell, borderBottom: 'none'}}><strong>{formatCurrency(incomeResults.totalMonthly)}</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {activeCalculator === 'budget' && budgetResults && (
            <div style={styles.results}>
              <h3 style={styles.resultsTitle}>{t("budgetResultsTitle")}</h3>
              <div style={styles.resultHighlight}>
                <div style={styles.highlightLabel}>{t("maxAffordablePrice")}</div>
                <div style={styles.highlightValue}>{formatCurrency(budgetResults.homePrice)}</div>
              </div>

              <div style={styles.resultGrid}>
                <div style={styles.resultItem}>
                  <div style={styles.resultItemLabel}>{t("monthlyBudgetLabel")}</div>
                  <div style={styles.resultItemValue}>{formatCurrency(budgetResults.monthlyBudget)}</div>
                </div>
                <div style={styles.resultItem}>
                  <div style={styles.resultItemLabel}>{t("loanAmount")}</div>
                  <div style={styles.resultItemValue}>{formatCurrency(budgetResults.loanAmount)}</div>
                </div>
                <div style={styles.resultItem}>
                  <div style={styles.resultItemLabel}>{t("downPaymentAmount")}</div>
                  <div style={styles.resultItemValue}>{formatCurrency(budgetResults.downPayment)}</div>
                </div>
              </div>

              <table style={styles.breakdownTable}>
                <thead>
                  <tr>
                    <th style={styles.tableHeader}>{t("monthlyCostBreakdown")}</th>
                    <th style={styles.tableHeader}>{t("amount")}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={styles.tableCell}>{t("principalInterest")}</td>
                    <td style={styles.tableCell}>{formatCurrency(budgetResults.breakdown.principalInterest)}</td>
                  </tr>
                  {budgetResults.includeFees && (
                    <>
                      <tr>
                        <td style={styles.tableCell}>{t("propertyTaxMonthly")}</td>
                        <td style={styles.tableCell}>{formatCurrency(budgetResults.breakdown.propertyTax)}</td>
                      </tr>
                      <tr>
                        <td style={styles.tableCell}>{t("hoaFeeMonthly")}</td>
                        <td style={styles.tableCell}>{formatCurrency(budgetResults.breakdown.hoa)}</td>
                      </tr>
                      <tr>
                        <td style={styles.tableCell}>{t("insuranceMonthly")}</td>
                        <td style={styles.tableCell}>{formatCurrency(budgetResults.breakdown.insurance)}</td>
                      </tr>
                      <tr>
                        <td style={styles.tableCell}>{t("maintenanceMonthly")}</td>
                        <td style={styles.tableCell}>{formatCurrency(budgetResults.breakdown.maintenance)}</td>
                      </tr>
                    </>
                  )}
                  <tr>
                    <td style={{...styles.tableCell, borderBottom: 'none'}}><strong>{t("totalMonthlyPayment")}</strong></td>
                    <td style={{...styles.tableCell, borderBottom: 'none'}}><strong>{formatCurrency(budgetResults.totalMonthly)}</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("introTitle")}</h2>
        <p style={styles.paragraph}>{t("introParagraph1")}</p>
        <p style={styles.paragraph}>{t("introParagraph2")}</p>
        <p style={styles.paragraph}>{t("introParagraph3")}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("howToUseTitle")}</h2>
        <ol style={styles.list}>
          <li style={styles.listItem}>{t("howToStep1")}</li>
          <li style={styles.listItem}>{t("howToStep2")}</li>
          <li style={styles.listItem}>{t("howToStep3")}</li>
          <li style={styles.listItem}>{t("howToStep4")}</li>
          <li style={styles.listItem}>{t("howToStep5")}</li>
          <li style={styles.listItem}>{t("howToStep6")}</li>
        </ol>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("insightsTitle")}</h2>

        <div style={styles.insightCard}>
          <h3 style={styles.h3}>{t("insight1Title")}</h3>
          <p style={styles.paragraph}>{t("insight1Content")}</p>
        </div>

        <div style={styles.insightCard}>
          <h3 style={styles.h3}>{t("insight2Title")}</h3>
          <p style={styles.paragraph}>{t("insight2Content")}</p>
        </div>

        <div style={styles.insightCard}>
          <h3 style={styles.h3}>{t("insight3Title")}</h3>
          <p style={styles.paragraph}>{t("insight3Content")}</p>
        </div>

        <div style={styles.insightCard}>
          <h3 style={styles.h3}>{t("insight4Title")}</h3>
          <p style={styles.paragraph}>{t("insight4Content")}</p>
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("detailsTitle")}</h2>

        <h3 style={styles.h3}>{t("detailsDTITitle")}</h3>
        <p style={styles.paragraph}>{t("detailsDTIContent")}</p>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t("detailsDTIItem1")}</li>
          <li style={styles.listItem}>{t("detailsDTIItem2")}</li>
          <li style={styles.listItem}>{t("detailsDTIItem3")}</li>
        </ul>

        <h3 style={styles.h3}>{t("detailsDownPaymentTitle")}</h3>
        <p style={styles.paragraph}>{t("detailsDownPaymentContent")}</p>

        <h3 style={styles.h3}>{t("detailsAdditionalCostsTitle")}</h3>
        <p style={styles.paragraph}>{t("detailsAdditionalCostsContent")}</p>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t("detailsCost1")}</li>
          <li style={styles.listItem}>{t("detailsCost2")}</li>
          <li style={styles.listItem}>{t("detailsCost3")}</li>
          <li style={styles.listItem}>{t("detailsCost4")}</li>
          <li style={styles.listItem}>{t("detailsCost5")}</li>
        </ul>

        <h3 style={styles.h3}>{t("detailsCreditScoreTitle")}</h3>
        <p style={styles.paragraph}>{t("detailsCreditScoreContent")}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("faqTitle")}</h2>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq1Question")}</h3>
          <p style={styles.faqAnswer}>{t("faq1Answer")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq2Question")}</h3>
          <p style={styles.faqAnswer}>{t("faq2Answer")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq3Question")}</h3>
          <p style={styles.faqAnswer}>{t("faq3Answer")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq4Question")}</h3>
          <p style={styles.faqAnswer}>{t("faq4Answer")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq5Question")}</h3>
          <p style={styles.faqAnswer}>{t("faq5Answer")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq6Question")}</h3>
          <p style={styles.faqAnswer}>{t("faq6Answer")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq7Question")}</h3>
          <p style={styles.faqAnswer}>{t("faq7Answer")}</p>
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("referencesTitle")}</h2>
        <ul style={styles.referenceList}>
          <li style={styles.referenceItem}>
            <a href="https://mechanics.bank/5-reasons-a-mortgage-affordability-calculator-is-a-key-tool-for-homebuyers/" style={styles.referenceLink} target="_blank" rel="noopener noreferrer">
              {t("reference1")}
            </a>
          </li>
          <li style={styles.referenceItem}>
            <a href="https://www.rocketmortgage.com/calculators/home-affordability-calculator" style={styles.referenceLink} target="_blank" rel="noopener noreferrer">
              {t("reference2")}
            </a>
          </li>
          <li style={styles.referenceItem}>
            <a href="https://www.firstmerchants.com/resources/learn/blogs/blog-detail/resource-library/2023/08/15/benefits-of-using-a-mortgage-calculator" style={styles.referenceLink} target="_blank" rel="noopener noreferrer">
              {t("reference3")}
            </a>
          </li>
          <li style={styles.referenceItem}>
            <a href="https://www.mpamag.com/us/mortgage-industry/guides/use-our-home-affordability-calculator-to-find-what-your-clients-can-afford/537084" style={styles.referenceLink} target="_blank" rel="noopener noreferrer">
              {t("reference4")}
            </a>
          </li>
          <li style={styles.referenceItem}>
            <a href="https://www.zillow.com/mortgage-calculator/house-affordability/" style={styles.referenceLink} target="_blank" rel="noopener noreferrer">
              {t("reference5")}
            </a>
          </li>
          <li style={styles.referenceItem}>
            <a href="https://www.wellsfargo.com/mortgage/calculators/home-affordability-calculator/" style={styles.referenceLink} target="_blank" rel="noopener noreferrer">
              {t("reference6")}
            </a>
          </li>
          <li style={styles.referenceItem}>
            <a href="https://www.realtor.com/mortgage/tools/affordability-calculator/" style={styles.referenceLink} target="_blank" rel="noopener noreferrer">
              {t("reference7")}
            </a>
          </li>
          <li style={styles.referenceItem}>
            <a href="https://www.range.com/financial-calculators/house-affordability-calculator" style={styles.referenceLink} target="_blank" rel="noopener noreferrer">
              {t("reference8")}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}